import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import QRCode from 'qrcode';
import { createContext, useContext, useEffect, useState } from 'react';
import { EuroField, parseEuro, useFormatMinutes } from '../components/forms';
import { Button, ErrorNote, Field } from '../components/ui';
import { WeekHoursEditor, type WeekHours } from '../components/WeekHoursEditor';
import { api, ApiError, type Salon } from '../lib/api';
import type { CatalogCategory } from '../lib/calendar';
import { useT, type TKey } from '../lib/i18n';
import { useCurrentLocation } from '../lib/location';
import { bookingUrl, ShareLinkButton } from './booking-settings';

type Business = {
  name: string;
  legalName: string | null;
  kvkNumber: string | null;
  vatNumber: string | null;
  kor: boolean;
  phone: string | null;
  email: string | null;
  address: { street: string | null; houseNumber: string | null; postcode: string | null; city: string | null };
};

const STEPS: TKey[] = ['welcome.step.salon', 'welcome.step.hours', 'welcome.step.services', 'welcome.step.team'];

/** What's being typed right now, so the preview can show it before anything is saved. */
type Draft = { address?: Business['address']; phone?: string | null; prices?: Record<string, string>; removed?: Set<string>; team?: string[] };
const DraftContext = createContext<(patch: Draft) => void>(() => undefined);
const useDraft = () => useContext(DraftContext);
const errorOf = (e: unknown, fallback: string) => (e instanceof ApiError ? e.message : e ? fallback : null);

/** The 4-step setup after signing up (plan 3.14): about five minutes, every step can be skipped. */
export function WelcomePage() {
  const { orgId } = useParams({ from: '/s/$orgId' });
  const t = useT();
  const qc = useQueryClient();
  const salon = useQuery({ queryKey: ['salon', orgId], queryFn: () => api<Salon>(`/orgs/${orgId}`) });
  const [step, setStep] = useState<number | null>(null);
  const [draft, setDraft] = useState<Draft>({});
  useEffect(() => {
    if (salon.data && step === null) setStep(Math.min(Math.max(salon.data.salon.onboardingStep, 1), 5));
  }, [salon.data, step]);
  const go = (next: number) => {
    setStep(next);
    window.scrollTo({ top: 0 });
    void api(`/orgs/${orgId}/onboarding`, { method: 'PUT', body: JSON.stringify({ step: next }) }).then(() => qc.invalidateQueries({ queryKey: ['salon', orgId] }));
  };
  if (!salon.data || step === null) return <p className="text-muted">{t('common.loading')}</p>;

  return (
    <DraftContext.Provider value={(patch) => setDraft((d) => ({ ...d, ...patch }))}>
      <div className="mx-auto grid max-w-6xl gap-10 lg:grid-cols-[minmax(0,1fr)_340px] lg:items-start xl:max-w-[1480px] xl:grid-cols-[270px_minmax(0,1fr)_340px] 2xl:grid-cols-[290px_minmax(0,1fr)_380px] 2xl:gap-14">
        <StepList step={step} />
        <div className="flex min-w-0 flex-col gap-7">
          {step <= 4 && (
            <ol className="grid grid-cols-4 gap-2 xl:hidden" aria-label={t('welcome.title')}>
              {STEPS.map((k, i) => (
                <li key={k} aria-current={i + 1 === step ? 'step' : undefined} className="flex flex-col gap-1.5">
                  <span className={`h-1 rounded-full ${i + 1 < step ? 'bg-ink/35' : i + 1 === step ? 'bg-ink' : 'bg-line'}`} />
                  <span className={`text-[12px] font-medium ${i + 1 === step ? 'text-ink' : 'text-muted'}`}>
                    {i + 1}. {t(k)}
                  </span>
                </li>
              ))}
            </ol>
          )}
          {step === 1 && <SalonStep orgId={orgId} onNext={() => go(2)} />}
          {step === 2 && <HoursStep orgId={orgId} onBack={() => go(1)} onNext={() => go(3)} />}
          {step === 3 && <ServicesStep orgId={orgId} onBack={() => go(2)} onNext={() => go(4)} />}
          {step === 4 && <TeamStep orgId={orgId} onBack={() => go(3)} onNext={() => go(5)} />}
          {step === 5 && <Live orgId={orgId} slug={salon.data.salon.slug} />}
        </div>
        <WizardPreview orgId={orgId} name={salon.data.salon.name} draft={draft} />
      </div>
    </DraftContext.Provider>
  );
}

const HINTS: TKey[] = ['welcome.hint.salon', 'welcome.hint.hours', 'welcome.hint.services', 'welcome.hint.team'];

/** Wide screens: the steps as a dark list on the desk, the current one marked with the ribbon. */
function StepList({ step }: { step: number }) {
  const t = useT();
  return (
    <aside className="hidden xl:block">
      <div className="sticky top-6 flex flex-col gap-6 overflow-hidden rounded-[var(--radius-panel)] bg-frame p-6 text-canvas shadow-[0_20px_50px_rgb(29_25_21/0.18)]">
        <p className="display text-[30px] leading-tight">{t('welcome.title')}</p>
        <ol className="flex flex-col gap-1">
          {[...STEPS, 'welcome.hint.live' as TKey].map((k, i) => {
            const n = i + 1;
            const done = n < step || (n === 5 && step >= 5);
            const current = n === step;
            return (
              <li key={k} aria-current={current ? 'step' : undefined} className={`relative flex gap-3 rounded-[14px] px-3 py-3 ${current ? 'bg-canvas/10' : ''}`}>
                {current && <span aria-hidden className="absolute -left-1 top-1/2 h-6 w-[3px] -translate-y-1/2 rounded-full bg-accent" />}
                <span
                  className={`flex size-7 shrink-0 items-center justify-center rounded-full text-[13px] font-semibold ${done ? 'bg-accent text-on-accent' : current ? 'bg-canvas text-ink' : 'border border-canvas/25 text-canvas/60'}`}
                >
                  {done ? '✓' : n}
                </span>
                <span className="min-w-0">
                  <span className={`block text-[15px] font-semibold ${current || done ? 'text-canvas' : 'text-canvas/60'}`}>{n <= 4 ? t(k) : t('welcome.hint.live')}</span>
                  {n <= 4 && <span className="block text-[13px] text-canvas/55">{t(HINTS[i]!)}</span>}
                </span>
              </li>
            );
          })}
        </ol>
        <p className="border-t border-canvas/10 pt-4 text-[13px] text-canvas/55">{t('welcome.later')}</p>
      </div>
    </aside>
  );
}

/**
 * The salon's own booking page on a phone, filling in as they type (live preview, as in a good
 * onboarding): name, address, services with their prices, and who you can book with.
 */
function WizardPreview({ orgId, name, draft }: { orgId: string; name: string; draft: Draft }) {
  const t = useT();
  const fmt = useFormatMinutes();
  const business = useQuery({ queryKey: ['business', orgId], queryFn: () => api<Business>(`/orgs/${orgId}/business`) });
  const catalog = useQuery({ queryKey: ['catalog', orgId], queryFn: () => api<{ categories: CatalogCategory[] }>(`/orgs/${orgId}/catalog`).then((r) => r.categories) });
  const address = draft.address ?? business.data?.address;
  const line = address ? [[address.street, address.houseNumber].filter(Boolean).join(' '), address.city].filter(Boolean).join(', ') : '';
  const services = (catalog.data ?? []).flatMap((c) => c.services).filter((s) => !draft.removed?.has(s.id)).slice(0, 5);
  const price = (s: { id: string; priceCents: number }) => {
    const typed = draft.prices?.[s.id];
    const cents = typed === undefined ? s.priceCents : (parseEuro(typed) ?? s.priceCents);
    return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(cents / 100);
  };
  return (
    <aside className="hidden lg:sticky lg:top-6 lg:flex lg:flex-col lg:gap-3" aria-label={t('welcome.preview.title')}>
      <p className="eyebrow">{t('welcome.preview.title')}</p>
      <div className="rounded-[44px] bg-frame p-2.5 shadow-[0_24px_60px_rgb(29_25_21/0.25)]">
        <div className="relative flex h-[600px] flex-col gap-4 overflow-hidden rounded-[36px] bg-canvas px-5 pb-5 pt-9">
          <span aria-hidden className="absolute left-1/2 top-2.5 h-5 w-24 -translate-x-1/2 rounded-full bg-frame" />
          <div className="relative">
            <p className="eyebrow !text-[9.5px]">{t('welcome.preview.label')}</p>
            <p className="display mt-1 text-[30px]">{name}</p>
            {line && <p className="mt-1 text-[12px] text-muted">{line}</p>}
            {(draft.phone ?? business.data?.phone) && <p className="text-[12px] font-semibold text-accent-strong">{draft.phone ?? business.data?.phone}</p>}
          </div>
          <div className="relative grid grid-cols-3 gap-1.5" aria-hidden>
            <span className="h-[3px] rounded-full bg-ink" />
            <span className="h-[3px] rounded-full bg-line" />
            <span className="h-[3px] rounded-full bg-line" />
          </div>
          <p className="display relative text-[22px]">{t('welcome.preview.choose')}</p>
          <ul className="relative flex flex-col overflow-hidden rounded-[14px] border border-line bg-surface">
            {services.map((s) => (
              <li key={s.id} className="flex items-center gap-2 border-b border-line px-3 py-2.5 last:border-b-0">
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] font-semibold">{s.name}</span>
                  <span className="block text-[11px] text-muted">{fmt(s.clientMinutes)}</span>
                </span>
                <span className="text-[12px] font-semibold tabular-nums">{price(s)}</span>
                <span aria-hidden className="flex size-5 items-center justify-center rounded-full border border-line text-[11px]">+</span>
              </li>
            ))}
            {services.length === 0 && <li className="px-3 py-4 text-[12px] text-muted">…</li>}
          </ul>
          {draft.team && draft.team.filter(Boolean).length > 0 && (
            <p className="relative text-[12px] text-muted">
              {t('welcome.preview.team')} {draft.team.filter(Boolean).join(', ')}
            </p>
          )}
          <div className="absolute inset-x-4 bottom-4 flex items-center justify-between rounded-full bg-frame py-1.5 pl-4 pr-1.5 text-canvas">
            <span className="text-[11px] text-canvas/70">{t('welcome.preview.bar')}</span>
            <span className="rounded-full bg-accent px-3 py-1.5 text-[11px] font-medium text-on-accent">{t('welcome.preview.next')}</span>
          </div>
        </div>
      </div>
    </aside>
  );
}

function StepFrame({ title, intro, children, onBack, onNext, busy, error }: { title: string; intro: string; children: React.ReactNode; onBack?: () => void; onNext: () => void; busy?: boolean; error?: string | null }) {
  const t = useT();
  return (
    <section className="flex flex-col gap-5">
      <div>
        <h1 className="page-title">{title}</h1>
        <p className="mt-3 max-w-prose text-muted">{intro}</p>
      </div>
      {children}
      <ErrorNote message={error ?? null} />
      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={onNext} disabled={busy}>
          {t('welcome.next')}
        </Button>
        {onBack && (
          <Button variant="ghost" onClick={onBack}>
            {t('welcome.back')}
          </Button>
        )}
      </div>
    </section>
  );
}

function SalonStep({ orgId, onNext }: { orgId: string; onNext: () => void }) {
  const t = useT();
  const business = useQuery({ queryKey: ['business', orgId], queryFn: () => api<Business>(`/orgs/${orgId}/business`) });
  const [form, setForm] = useState<Business | null>(null);
  useEffect(() => {
    if (business.data && !form) setForm(business.data);
  }, [business.data, form]);
  const save = useMutation({ mutationFn: (b: Business) => api(`/orgs/${orgId}/business`, { method: 'PUT', body: JSON.stringify(b) }), onSuccess: onNext });
  const addr = form?.address;
  // Street and city from postcode + house number (PDOK).
  useEffect(() => {
    if (!addr) return;
    const pc = (addr.postcode ?? '').replace(/\s/g, '').toUpperCase();
    if (!/^\d{4}[A-Z]{2}$/.test(pc) || !addr.houseNumber?.trim()) return;
    const id = window.setTimeout(() => {
      void api<{ address: { street: string; city: string } | null }>(`/address?${new URLSearchParams({ postcode: pc, number: addr.houseNumber! })}`).then((r) => {
        if (r.address) setForm((f) => (f ? { ...f, address: { ...f.address, street: r.address!.street, city: r.address!.city } } : f));
      });
    }, 400);
    return () => window.clearTimeout(id);
  }, [addr?.postcode, addr?.houseNumber]);
  const share = useDraft();
  useEffect(() => {
    if (form) share({ address: form.address, phone: form.phone });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);
  if (!form) return <p className="text-muted">{t('common.loading')}</p>;
  const set = (k: keyof Business['address'], v: string) => setForm({ ...form, address: { ...form.address, [k]: v } });
  return (
    <StepFrame title={t('welcome.salon.title')} intro={t('welcome.salon.intro')} onNext={() => save.mutate(form)} busy={save.isPending} error={errorOf(save.error, t('error.generic'))}>
      <div className="grid grid-cols-2 gap-3">
        <Field id="w-postcode" label={t('loc.postcode')} value={form.address.postcode ?? ''} onChange={(e) => set('postcode', e.target.value)} autoComplete="postal-code" />
        <Field id="w-number" label={t('loc.houseNumber')} value={form.address.houseNumber ?? ''} onChange={(e) => set('houseNumber', e.target.value)} />
      </div>
      <Field id="w-street" label={t('loc.street')} value={form.address.street ?? ''} onChange={(e) => set('street', e.target.value)} />
      <Field id="w-city" label={t('loc.city')} value={form.address.city ?? ''} onChange={(e) => set('city', e.target.value)} />
      <Field id="w-phone" type="tel" label={t('welcome.salon.phone')} hint={t('welcome.salon.phoneHint')} value={form.phone ?? ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
    </StepFrame>
  );
}

function HoursStep({ orgId, onBack, onNext }: { orgId: string; onBack: () => void; onNext: () => void }) {
  const t = useT();
  const location = useCurrentLocation(orgId);
  const locId = location.data?.id;
  const hours = useQuery({ queryKey: ['hours', locId], queryFn: () => api<{ hours: WeekHours }>(`/orgs/${orgId}/locations/${locId}/hours`).then((r) => r.hours), enabled: !!locId });
  const [draft, setDraft] = useState<WeekHours | null>(null);
  useEffect(() => {
    if (hours.data && !draft) setDraft(hours.data);
  }, [hours.data, draft]);
  const save = useMutation({ mutationFn: (h: WeekHours) => api(`/orgs/${orgId}/locations/${locId}/hours`, { method: 'PUT', body: JSON.stringify({ hours: h }) }), onSuccess: onNext });
  if (!draft) return <p className="text-muted">{t('common.loading')}</p>;
  return (
    <StepFrame title={t('welcome.hours.title')} intro={t('welcome.hours.intro')} onBack={onBack} onNext={() => save.mutate(draft)} busy={save.isPending} error={errorOf(save.error, t('error.generic'))}>
      <div className="rounded-[var(--radius-card)] border border-line bg-surface px-4">
        <WeekHoursEditor value={draft} onChange={setDraft} closedLabel={t('hours.closed')} />
      </div>
    </StepFrame>
  );
}

function ServicesStep({ orgId, onBack, onNext }: { orgId: string; onBack: () => void; onNext: () => void }) {
  const t = useT();
  const fmt = useFormatMinutes();
  const qc = useQueryClient();
  const key = ['catalog', orgId];
  const catalog = useQuery({ queryKey: key, queryFn: () => api<{ categories: CatalogCategory[] }>(`/orgs/${orgId}/catalog`).then((r) => r.categories) });
  const template = useMutation({ mutationFn: () => api(`/orgs/${orgId}/catalog/template`, { method: 'POST' }), onSuccess: () => qc.invalidateQueries({ queryKey: key }) });
  const [prices, setPrices] = useState<Record<string, string>>({});
  const [removed, setRemoved] = useState<Set<string>>(new Set());
  const share = useDraft();
  useEffect(() => {
    share({ prices, removed });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prices, removed]);
  // Start from the business type's template when there's nothing yet.
  const empty = catalog.data && catalog.data.every((c) => c.services.length === 0);
  useEffect(() => {
    if (empty && !template.isPending && !template.isSuccess) template.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empty]);
  const save = useMutation({
    mutationFn: async () => {
      for (const cat of catalog.data ?? []) {
        for (const s of cat.services) {
          if (removed.has(s.id)) {
            await api(`/orgs/${orgId}/services/${s.id}`, { method: 'DELETE' });
            continue;
          }
          const typed = prices[s.id];
          const cents = typed === undefined ? null : parseEuro(typed);
          if (cents === null || cents === s.priceCents) continue;
          // The catalog sends the full service (duration, VAT, variants); send it back with the new price.
          type Full = Omit<CatalogCategory['services'][number], 'variants'> & { description: string | null; vatRate: string; onlineBookable: boolean; duration: unknown; variants: Array<{ id: string; name: string; priceCents: number; duration: unknown }> };
          const full = s as unknown as Full;
          await api(`/orgs/${orgId}/services/${s.id}`, {
            method: 'PUT',
            body: JSON.stringify({
              categoryId: cat.id,
              name: s.name,
              description: full.description,
              priceType: s.priceType,
              priceCents: cents,
              duration: full.duration,
              vatRate: full.vatRate,
              onlineBookable: full.onlineBookable,
              staff: s.staff,
              variants: full.variants.map((v) => ({ id: v.id, name: v.name, priceCents: v.priceCents, duration: v.duration })),
            }),
          });
        }
      }
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: key });
      onNext();
    },
  });
  return (
    <StepFrame title={t('welcome.services.title')} intro={t('welcome.services.intro')} onBack={onBack} onNext={() => save.mutate()} busy={save.isPending || template.isPending} error={errorOf(save.error ?? template.error, t('error.generic'))}>
      {catalog.data?.map((cat) => (
        <div key={cat.id} className="overflow-hidden rounded-[var(--radius-card)] border border-line bg-surface">
          <p className="border-b border-line bg-subtle px-4 py-2 font-semibold">{cat.name}</p>
          <ul className="divide-y divide-line">
            {cat.services.map((s) =>
              removed.has(s.id) ? (
                <li key={s.id} className="flex items-center justify-between px-4 py-2 text-sm text-muted">
                  <span className="line-through">{s.name}</span>
                  <button type="button" className="font-semibold text-accent-strong hover:underline" onClick={() => setRemoved((r) => new Set([...r].filter((x) => x !== s.id)))}>
                    {t('welcome.services.keep')}
                  </button>
                </li>
              ) : (
                <li key={s.id} className="flex items-center gap-3 px-4 py-2">
                  <span className="min-w-0 flex-1">
                    <span className="block font-medium">{s.name}</span>
                    <span className="text-sm text-muted">{fmt(s.clientMinutes)}</span>
                  </span>
                  <div className="w-28">
                    <EuroField id={`price-${s.id}`} label={`${t('welcome.services.price')}: ${s.name}`} hideLabel value={prices[s.id] ?? (s.priceCents / 100).toFixed(2).replace('.', ',')} onChange={(v) => setPrices((p) => ({ ...p, [s.id]: v }))} />
                  </div>
                  <button type="button" aria-label={`${t('common.delete')}: ${s.name}`} className="min-h-11 rounded-[var(--radius-control)] px-2 text-muted hover:bg-subtle" onClick={() => setRemoved((r) => new Set([...r, s.id]))}>
                    ✕
                  </button>
                </li>
              ),
            )}
          </ul>
        </div>
      ))}
      <p className="text-sm text-muted">{t('welcome.services.later')}</p>
    </StepFrame>
  );
}

function TeamStep({ orgId, onBack, onNext }: { orgId: string; onBack: () => void; onNext: () => void }) {
  const t = useT();
  const qc = useQueryClient();
  const [alone, setAlone] = useState(true);
  const [names, setNames] = useState<string[]>(['']);
  const share = useDraft();
  useEffect(() => {
    share({ team: alone ? [] : names });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alone, names]);
  const save = useMutation({
    mutationFn: async () => {
      if (alone) return;
      for (const n of names.map((x) => x.trim()).filter(Boolean)) await api(`/orgs/${orgId}/team`, { method: 'POST', body: JSON.stringify({ displayName: n }) });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['team', orgId] });
      onNext();
    },
  });
  return (
    <StepFrame title={t('welcome.team.title')} intro={t('welcome.team.intro')} onBack={onBack} onNext={() => save.mutate()} busy={save.isPending} error={errorOf(save.error, t('error.generic'))}>
      <fieldset className="flex flex-col gap-2">
        {[true, false].map((v) => (
          <label key={String(v)} className={`flex cursor-pointer items-center gap-3 rounded-[var(--radius-control)] border px-4 py-3 ${alone === v ? 'border-ink bg-surface shadow-[0_0_0_1px_var(--color-ink)]' : 'border-line bg-surface'}`}>
            <input type="radio" name="team" className="size-5 accent-[var(--color-ink)]" checked={alone === v} onChange={() => setAlone(v)} />
            {v ? t('welcome.team.alone') : t('welcome.team.more')}
          </label>
        ))}
      </fieldset>
      {!alone && (
        <div className="flex flex-col gap-2">
          {names.map((n, i) => (
            <Field key={i} id={`w-name-${i}`} label={t('welcome.team.name', { n: i + 1 })} value={n} onChange={(e) => setNames(names.map((x, j) => (j === i ? e.target.value : x)))} />
          ))}
          <button type="button" className="self-start text-sm font-semibold text-accent-strong hover:underline" onClick={() => setNames([...names, ''])}>
            + {t('welcome.team.add')}
          </button>
          <p className="text-sm text-muted">{t('welcome.team.inviteLater')}</p>
        </div>
      )}
    </StepFrame>
  );
}

function Live({ orgId, slug }: { orgId: string; slug: string }) {
  const t = useT();
  const navigate = useNavigate();
  const [qr, setQr] = useState<string | null>(null);
  const url = bookingUrl(slug);
  useEffect(() => {
    void QRCode.toDataURL(url, { margin: 1, width: 320 }).then(setQr);
  }, [url]);
  return (
    <section className="flex flex-col items-start gap-5">
      <span aria-hidden className="flex size-14 items-center justify-center rounded-full bg-accent text-2xl text-on-accent shadow-[0_0_30px_rgb(255_148_102/0.5)]">
        ✓
      </span>
      <div>
        <h1 className="page-title">{t('welcome.live.title')}</h1>
        <p className="mt-1 text-muted">{t('welcome.live.intro')}</p>
      </div>
      <p className="break-all rounded-[var(--radius-control)] bg-subtle px-3 py-2 font-medium">{url}</p>
      <div className="flex flex-wrap gap-2">
        <ShareLinkButton orgId={orgId} slug={slug} />
        <a href={url} target="_blank" rel="noreferrer" className="inline-flex min-h-11 items-center rounded-full px-4 text-[15px] font-semibold text-accent-strong hover:bg-subtle">
          {t('booking.open')} ↗
        </a>
      </div>
      {qr && <img src={qr} alt={t('booking.qrAlt')} className="size-40 rounded-[var(--radius-control)] border border-line" />}
      <p className="max-w-prose text-sm text-muted">{t('welcome.live.instagram')}</p>
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => navigate({ to: '/s/$orgId/vandaag', params: { orgId } })}>{t('welcome.live.done')}</Button>
        <Link to="/s/$orgId/salon/online-boeken" params={{ orgId }} className="inline-flex min-h-11 items-center rounded-full px-4 text-[15px] font-semibold text-accent-strong hover:bg-subtle">
          {t('welcome.live.website')}
        </Link>
      </div>
    </section>
  );
}

